import { useMemo, useState } from 'react';
import { ExternalLink, Loader2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { MrsChip } from './mrs-hover-card';
import { StatusPill } from './PicklistProgressBits';
import type { PicklistDto, PicklistItemDto, PostingBreakdownRow } from '../types';
import { qty } from '../utils/picklistProgress';
import { phtFullDate, phtTimeOfDay } from '@/features/activity/lib/phtDay';

/**
 * PW-26 — every Material Issuance Slip this picklist has produced, and what each one actually issued.
 *
 * ## Why this card exists rather than links in the Items table
 *
 * The Status column used to carry one link per MIS with no quantity beside it, because the item-level
 * `misReferences` has none. So a picklist posted in three batches showed three identical-looking links
 * and no way to tell what any of them contained. Grouping by SLIP instead of by line is the shape that
 * can answer "what did MIS-4471 issue" — which is the question someone reconciling against NetSuite is
 * actually asking.
 *
 * ## Everything here is read back, not re-derived
 *
 * Rows come from the posting-breakdown projection over the issuance ledger — the per-(bin, serial/lot)
 * record of what went out. The issuance reason and requested-by are the values PW-25 froze onto the batch
 * at post time, not the picklist's current state, because the current state cannot reconstruct what was
 * SENT. Anything genuinely absent renders as an em-dash; nothing is substituted.
 */

interface PostedToNetSuiteCardProps {
  picklist: PicklistDto;
  rows: PostingBreakdownRow[] | null;
  loading: boolean;
  error: string | null;
  onLoad: () => void;
}

/** One slip: its identity, its rows, and the header fields it was posted with. */
interface Slip {
  key: string;
  misId: string | null;
  misTranId: string | null;
  misUrl: string;
  postedAt: string | null;
  postedByName: string | null;
  reason: string | null;
  requestedBy: string | null;
  rows: PostingBreakdownRow[];
}

function groupIntoSlips(rows: PostingBreakdownRow[]): Slip[] {
  const byMis = new Map<string, Slip>();
  for (const row of rows) {
    // A settled batch can legitimately carry a NetSuite id and no MIS-xxxx number for a short window
    // while the tranid backfill runs, so the id is the key and the number is display only.
    const key = row.misId ?? row.misTranId ?? 'unknown';
    const existing = byMis.get(key);
    if (existing) {
      existing.rows.push(row);
      continue;
    }
    byMis.set(key, {
      key,
      misId: row.misId,
      misTranId: row.misTranId,
      misUrl: row.misUrl,
      postedAt: row.postedAt,
      postedByName: row.postedByName,
      reason: row.reasonForAdjustmentName,
      requestedBy: row.requestedByName,
      rows: [row],
    });
  }
  return [...byMis.values()].sort((a, b) => {
    const at = a.postedAt ? new Date(a.postedAt).getTime() : 0;
    const bt = b.postedAt ? new Date(b.postedAt).getTime() : 0;
    return bt - at; // newest first
  });
}

const misLabel = (slip: Slip) => slip.misTranId ?? (slip.misId ? `MIS #${slip.misId}` : 'MIS pending');

/**
 * Totals are PER UNIT OF MEASURE — never one summed number, and never a total that silently omits a
 * serialized line. "2 lines · 6 pcs · 1 unit issued", not "7".
 */
function totalsByUnit(rows: PostingBreakdownRow[], items: PicklistItemDto[]): string {
  const unitOf = new Map(items.map((i) => [i.id, i.unitOfMeasure]));
  const sums = new Map<string, number>();
  for (const r of rows) {
    const unit = unitOf.get(r.picklistItemId) ?? 'units';
    sums.set(unit, (sums.get(unit) ?? 0) + r.quantity);
  }
  return [...sums.entries()].map(([unit, total]) => `${qty(total)} ${unit}`).join(' · ');
}

/**
 * The slip row's single "Issued" figure (owner 2026-10-08: "consolidate the units"). One unit reads as itself — "2 SET";
 * mixed units sum into one generic total — "27 units", the wording the rest of the app uses for a mixed-unit total
 * (86d43a7cq). The per-unit breakdown stays in the slip dialog's footer.
 */
function consolidatedTotal(rows: PostingBreakdownRow[], items: PicklistItemDto[]): string {
  const unitOf = new Map(items.map((i) => [i.id, i.unitOfMeasure]));
  const units = new Set(rows.map((r) => unitOf.get(r.picklistItemId) ?? ''));
  const total = rows.reduce((sum, r) => sum + r.quantity, 0);
  const [only] = units;
  return units.size === 1 && only ? `${qty(total)} ${only}` : `${qty(total)} units`;
}

export function PostedToNetSuiteCard({ picklist, rows, loading, error, onLoad }: PostedToNetSuiteCardProps) {
  const [openSlip, setOpenSlip] = useState<Slip | null>(null);
  // Owner 2026-10-08 — the list can be hidden again. Hiding keeps the rows: showing them again does not re-read.
  const [hidden, setHidden] = useState(false);
  const showSlips = () => {
    setHidden(false);
    if (rows === null) onLoad();
  };

  // The card exists only once something has posted. Deriving that from the picklist rather than from the
  // breakdown means it appears without waiting for the fetch — and does not flash in for a picklist that
  // has never posted.
  const hasPosted = (picklist.items ?? []).some((i) => i.quantityPosted > 0);
  const slips = useMemo(() => groupIntoSlips(rows ?? []), [rows]);

  if (!hasPosted) return null;

  return (
    <div className="overflow-hidden rounded-xl border border-success/30 bg-card">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold">Posted to NetSuite</p>
          <p className="text-xs text-muted-foreground">
            {slips.length > 0 ? `${slips.length} slip${slips.length === 1 ? '' : 's'} · ` : ''}
            only these quantities have moved NetSuite inventory
          </p>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <span
              tabIndex={0}
              className="cursor-help text-xs text-muted-foreground underline decoration-dotted underline-offset-4"
            >
              What is an MIS?
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-xs">
            A Material Issuance Slip is the NetSuite document created when picked stock is posted. It is the
            only step that moves inventory — picking alone does not.
          </TooltipContent>
        </Tooltip>
      </div>

      {(rows === null || hidden) && !loading && !error && (
        <div className="px-4 py-3">
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={showSlips}>
            Show the slips
          </Button>
        </div>
      )}

      {loading && (
        <p className="flex items-center gap-2 px-4 py-3 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden />
          Reading what each slip issued…
        </p>
      )}

      {error && (
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <p className="text-xs text-destructive">{error}</p>
          <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onLoad}>
            Retry
          </Button>
        </div>
      )}

      {!hidden && (
        <div className="divide-y divide-border">
          {slips.map((slip) => (
            <button
              key={slip.key}
              type="button"
              onClick={() => setOpenSlip(slip)}
              className="grid w-full gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring md:grid-cols-[minmax(140px,auto)_minmax(0,1fr)_auto]"
            >
              <div className="space-y-1">
                <StatusPill
                  state={{ tone: 'success', label: 'Posted', tooltip: 'This slip has moved NetSuite inventory.' }}
                />
                <p className="font-mono text-xs font-semibold">{misLabel(slip)}</p>
              </div>

              {/* Owner 2026-10-08: the slip's facts, not its items — the items are one click away in the slip. */}
              {/* Fixed widths, so each fact sits at the same x on every slip; they WRAP when the card is narrow (the
                  side panels can leave it ~280px), never overflow. The card's width, not the window's, decides. */}
              <div
                className="flex min-w-0 flex-wrap items-start gap-x-6 gap-y-2 self-center"
                title="Click to see what was issued"
              >
                <SlipFact label="Lines" value={String(misLineCount(slip.rows))} className="w-12 shrink-0" />
                <SlipFact label="Issued" value={consolidatedTotal(slip.rows, picklist.items)} className="w-24 shrink-0" />
                <SlipFact
                  label="Posted"
                  value={slip.postedAt ? `${phtFullDate(slip.postedAt)}, ${phtTimeOfDay(slip.postedAt)}` : null}
                  className="w-44 shrink-0"
                />
                <SlipFact label="Requested by" value={slip.requestedBy} className="min-w-[140px] flex-1" />
              </div>

              <div className="flex items-start">
                {slip.misUrl && (
                  <span
                    role="link"
                    tabIndex={-1}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border px-2.5 text-xs"
                    onClick={(e) => { e.stopPropagation(); window.open(slip.misUrl, '_blank', 'noopener'); }}
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                    Open in NetSuite
                  </span>
                )}
              </div>
            </button>
          ))}
          {slips.length > 0 && (
            <div className="px-4 py-2.5">
              <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => setHidden(true)}>
                Hide the slips
              </Button>
            </div>
          )}
        </div>
      )}

      <SlipDialog
        slip={openSlip}
        picklist={picklist}
        onOpenChange={(open) => { if (!open) setOpenSlip(null); }}
      />
    </div>
  );
}

/**
 * The MIS's own line count: one NetSuite line per MRS line (MisLineKey with LinePerMrsLine on, ISS-R-177), bins and
 * serials being that line's inventory detail. The breakdown rows are per bin slot and serial, so counting them read
 * "5 lines" for MIS-000036980's 4, and would read "60 lines" for a 60-serial slip. A slip posted before the switch
 * merged two lines of one item on one MRS into one NetSuite line, so on such a slip this can read one high.
 */
function misLineCount(rows: PostingBreakdownRow[]): number {
  return new Set(rows.map((r) => r.picklistItemId)).size;
}

/** One labelled fact of a slip row. An absent value reads as an em-dash, never as an invented one. */
function SlipFact({ label, value, className }: { label: string; value: string | null | undefined; className?: string }) {
  return (
    <div className={cn('min-w-0', className)}>
      <p className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="truncate text-sm tabular-nums text-foreground" title={value ?? undefined}>
        {value || '—'}
      </p>
    </div>
  );
}

function Fact({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className="text-xs text-muted-foreground">{label}</span>
      {/* An em-dash, never a substitute. A slip is a record of what was sent; a plausible-looking value
          invented from current state would be a different document. */}
      <span className="text-xs font-semibold">{value || '—'}</span>
    </div>
  );
}

function SlipDialog({
  slip,
  picklist,
  onOpenChange,
}: {
  slip: Slip | null;
  picklist: PicklistDto;
  onOpenChange: (open: boolean) => void;
}) {
  if (!slip) return null;

  const byItem = new Map<number, PostingBreakdownRow[]>();
  for (const row of slip.rows) {
    byItem.set(row.picklistItemId, [...(byItem.get(row.picklistItemId) ?? []), row]);
  }

  // PT-7 A10 — a block is one MRS line (the breakdown row's `picklistItemId` is the posting line). Two blocks of
  // ONE item (one `itemGroups[]` entry) on the SAME MRS number read identically, so each adds its line's
  // remarks: told apart by remarks, never by an ordinal (OWNER_DECISIONS §C).
  const itemOfLine = new Map<number, number>();
  for (const group of picklist.itemGroups ?? []) {
    for (const line of group.plannedLines ?? []) itemOfLine.set(line.id, group.id);
  }
  const sameItemSameMrs = (lineId: number) => {
    const mrs = picklist.items.find((i) => i.id === lineId)?.mrsNumber ?? '';
    return `${itemOfLine.get(lineId) ?? lineId}|${mrs}`;
  };
  const blocksPerItemMrs = new Map<string, number>();
  for (const lineId of byItem.keys()) {
    const k = sameItemSameMrs(lineId);
    blocksPerItemMrs.set(k, (blocksPerItemMrs.get(k) ?? 0) + 1);
  }

  // A slip can serve several MRS, which is why the requests get their own row rather than a facts cell.
  const mrsOnSlip = [...new Set(
    [...byItem.keys()].map((id) => picklist.items.find((i) => i.id === id)?.mrsNumber).filter(Boolean),
  )] as string[];

  return (
    <Dialog open onOpenChange={onOpenChange}>
      {/* Focus the slip itself on open, not its first control: that is the Posted pill, whose tooltip then opened
          over the slip every time (owner 2026-10-08). Radix fires this on the content, which is focusable. */}
      <DialogContent
        className="max-h-[90vh] max-w-[700px] overflow-y-auto outline-none"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          (event.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <DialogHeader>
          <DialogTitle className="flex flex-wrap items-center gap-2 font-mono text-base">
            {misLabel(slip)}
            <StatusPill
              state={{ tone: 'success', label: 'Posted to NetSuite', tooltip: 'This slip has moved inventory.' }}
            />
          </DialogTitle>
          <DialogDescription>
            Material Issuance Slip
            {slip.postedAt && ` · posted ${new Date(slip.postedAt).toLocaleString()}`}
            {slip.postedByName && ` by ${slip.postedByName}`}
          </DialogDescription>
        </DialogHeader>

        {mrsOnSlip.length > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border px-3 py-2">
            <span className="text-xs text-muted-foreground">Material requests</span>
            <div className="flex min-w-0 flex-wrap gap-2" data-mrs-chip-group="">
              {mrsOnSlip.map((mrs) => {
                const item = picklist.items.find((i) => i.mrsNumber === mrs);
                // The current MRS chip (owner 2026-10-08): hover or focus opens the page's one MRS card.
                return item ? (
                  <MrsChip
                    key={mrs}
                    picklistId={picklist.id}
                    picklistCreatedAt={picklist.createdAt}
                    materialRequestId={item.materialRequestId}
                    mrsNumber={mrs}
                    requestorFullName={item.requestorFullName}
                    departmentName={item.departmentName}
                  />
                ) : (
                  <span key={mrs} className="font-mono text-xs">{mrs}</span>
                );
              })}
            </div>
            <span className="text-xs text-muted-foreground">
              {mrsOnSlip.length} request{mrsOnSlip.length === 1 ? '' : 's'}
            </span>
          </div>
        )}

        <div className="grid gap-x-6 gap-y-1.5 rounded-lg bg-muted/35 p-3 sm:grid-cols-2">
          <Fact label="Picklist" value={picklist.picklistNumber} />
          <Fact label="Issuance reason" value={slip.reason} />
          <Fact label="Requested by" value={slip.requestedBy} />
          <Fact label="Posted by" value={slip.postedByName} />
          <Fact
            label="Posted at"
            value={slip.postedAt ? new Date(slip.postedAt).toLocaleString() : null}
          />
        </div>

        <div className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Items issued</p>
          {[...byItem.entries()].map(([itemId, rows]) => {
            const item = picklist.items.find((i) => i.id === itemId);
            const lineTotal = rows.reduce((acc, r) => acc + r.quantity, 0);
            const showRemarks = (blocksPerItemMrs.get(sameItemSameMrs(itemId)) ?? 0) > 1 && !!item?.remarks;
            return (
              <div key={itemId} className="overflow-hidden rounded-lg border border-border">
                <div className="flex flex-wrap items-start justify-between gap-2 bg-muted/30 px-3 py-2">
                  <div className="min-w-0">
                    <p className="font-mono text-sm font-semibold">{item?.itemCode ?? '—'}</p>
                    <p className="truncate text-xs text-muted-foreground" title={item?.itemName}>{item?.itemName}</p>
                  </div>
                  <div className="text-right">
                    {/* A serialized line shows a QUANTITY here, never a serial number — the serials are
                        one per bin row below, and putting one up here would name a single unit as if it
                        were the whole line. */}
                    <p className="text-sm font-bold tabular-nums">
                      {qty(lineTotal)} {item?.unitOfMeasure}
                    </p>
                    <p className="font-mono text-xs text-muted-foreground">{item?.mrsNumber}</p>
                    {showRemarks && (
                      <p className="ml-auto max-w-64 truncate text-xs text-muted-foreground" title={item?.remarks}>
                        {item?.remarks}
                      </p>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-[1fr_1fr_auto] gap-2 border-b border-border px-3 py-1.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
                  <span>Bin</span>
                  <span>Lot / Serial</span>
                  <span className="text-right">Quantity</span>
                </div>
                {rows.map((row, index) => (
                  <div
                    key={`${itemId}-${index}`}
                    className="grid grid-cols-[1fr_1fr_auto] gap-2 px-3 py-1.5 text-xs"
                  >
                    <span className="font-mono">{row.binCode}</span>
                    <span className="font-mono text-muted-foreground">
                      {row.issuedInventoryNumber
                        ?? (row.issuedInventoryNumberId ? `#${row.issuedInventoryNumberId}` : '—')}
                    </span>
                    <span className="text-right tabular-nums">{qty(row.quantity)}</span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>

        <DialogFooter className="sm:justify-between">
          <p className="text-xs text-muted-foreground">
            {byItem.size} line{byItem.size === 1 ? '' : 's'} · {totalsByUnit(slip.rows, picklist.items)} issued
          </p>
          <div className="flex gap-2">
            {slip.misUrl && (
              <Button variant="outline" size="sm" onClick={() => window.open(slip.misUrl, '_blank', 'noopener')}>
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                Open in NetSuite
              </Button>
            )}
            <Button size="sm" onClick={() => onOpenChange(false)}>
              <Send className="mr-1.5 h-3.5 w-3.5" />
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
